import React, { useState, useEffect } from 'react';
import { ordersApi, Order } from '../../services/api';
import './OrdersManagement.css';

const OrdersManagement: React.FC = () => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<Order | null>(null);
    const [editForm, setEditForm] = useState({
        status: '',
        trackingId: '',
        courierPartner: '',
        customCourierName: '',
    });
    const [filters, setFilters] = useState({
        status: '',
        email: '',
        startDate: '',
        endDate: '',
    });

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const response = await ordersApi.getAll();
            setOrders(response.data.orders || []);
        } catch (err) {
            setError('Failed to fetch orders');
            console.error('Error fetching orders:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (order: Order) => {
        setEditingOrder(order);
        const courierPartner = order.courierPartner || '';
        const standardCouriers = ['Blue Dart', 'India Post', 'DTDC', 'FedEx', 'DHL', 'Delhivery', 'XpressBees', 'Ecom Express'];
        const isCustomCourier = courierPartner && !standardCouriers.includes(courierPartner);

        setEditForm({
            status: order.status || '',
            trackingId: order.trackingId || '',
            courierPartner: isCustomCourier ? 'Other' : courierPartner,
            customCourierName: isCustomCourier ? courierPartner : '',
        });
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingOrder) return;

        // Validate custom courier name if "Other" is selected
        if (editForm.courierPartner === 'Other' && !editForm.customCourierName.trim()) {
            setError('Please enter the courier partner name');
            return;
        }

        try {
            setError(null);
            const courierPartner = editForm.courierPartner === 'Other'
                ? editForm.customCourierName.trim()
                : editForm.courierPartner;

            await ordersApi.update(editingOrder.id, {
                status: editForm.status,
                trackingId: editForm.trackingId || undefined,
                courierPartner: courierPartner || undefined,
            });
            await fetchOrders();
            setEditingOrder(null);
            setEditForm({ status: '', trackingId: '', courierPartner: '', customCourierName: '' });
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 'Failed to update order';
            setError(errorMessage);
            console.error('Error updating order:', err);
        }
    };

    const cancelEdit = () => {
        setEditingOrder(null);
        setEditForm({ status: '', trackingId: '', courierPartner: '', customCourierName: '' });
        setError(null);
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending':
                return '#ffc107';
            case 'confirmed':
                return '#17a2b8';
            case 'shipped':
                return '#28a745';
            case 'delivered':
                return '#007bff';
            case 'cancelled':
                return '#dc3545';
            default:
                return '#6c757d';
        }
    };

    const handleFilterChange = (key: string, value: string) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            email: '',
            startDate: '',
            endDate: '',
        });
    };

    const filteredOrders = orders.filter(order => {
        // Filter by status
        if (filters.status && order.status.toLowerCase() !== filters.status.toLowerCase()) {
            return false;
        }

        // Filter by email
        if (filters.email) {
            const email = order.userEmail || '';
            if (!email.toLowerCase().includes(filters.email.toLowerCase())) {
                return false;
            }
        }

        // Filter by date range
        if (filters.startDate || filters.endDate) {
            const orderDate = new Date(order.createdAt);
            orderDate.setHours(0, 0, 0, 0);

            if (filters.startDate) {
                const startDate = new Date(filters.startDate);
                startDate.setHours(0, 0, 0, 0);
                if (orderDate < startDate) {
                    return false;
                }
            }

            if (filters.endDate) {
                const endDate = new Date(filters.endDate);
                endDate.setHours(23, 59, 59, 999);
                if (orderDate > endDate) {
                    return false;
                }
            }
        }

        return true;
    });

    const hasActiveFilters = filters.status || filters.email || filters.startDate || filters.endDate;

    if (loading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="orders-management">
            <div className="section-header">
                <h2>Orders Management</h2>
                <button
                    className="btn btn-secondary"
                    onClick={fetchOrders}
                >
                    Refresh
                </button>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Filters Section */}
            <div className="filters-section">
                <div className="filters-header">
                    <h3>Filters</h3>
                    {hasActiveFilters && (
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={clearFilters}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
                <div className="filters-grid">
                    <div className="filter-group">
                        <label htmlFor="filter-status">Status</label>
                        <select
                            id="filter-status"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label htmlFor="filter-email">Customer Email</label>
                        <input
                            type="text"
                            id="filter-email"
                            value={filters.email}
                            onChange={(e) => handleFilterChange('email', e.target.value)}
                            placeholder="Search by email..."
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="filter-start-date">Start Date</label>
                        <input
                            type="date"
                            id="filter-start-date"
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label htmlFor="filter-end-date">End Date</label>
                        <input
                            type="date"
                            id="filter-end-date"
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        />
                    </div>
                </div>
                {hasActiveFilters && (
                    <div className="filter-results">
                        Showing {filteredOrders.length} of {orders.length} orders
                    </div>
                )}
            </div>

            {orders.length === 0 ? (
                <div className="empty-state">
                    <p>No orders found.</p>
                </div>
            ) : filteredOrders.length === 0 ? (
                <div className="empty-state">
                    <p>No orders match the selected filters.</p>
                </div>
            ) : (
                <div className="orders-table-container">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Email</th>
                                <th>Items</th>
                                <th>Total</th>
                                <th>Status</th>
                                <th>Tracking ID</th>
                                <th>Courier Partner</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((order) => (
                                <tr key={order.id}>
                                    <td className="order-id">{order.id}</td>
                                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                                    <td>{order.userId}</td>
                                    <td>{order.userEmail || 'N/A'}</td>
                                    <td>{order.products.length} item(s)</td>
                                    <td>₹{order.totalAmount?.toFixed(2) || '0.00'}</td>
                                    <td>
                                        <span
                                            className="status-badge"
                                            style={{ backgroundColor: getStatusColor(order.status) }}
                                        >
                                            {order.status}
                                        </span>
                                    </td>
                                    <td>
                                        {order.trackingId ? (
                                            <span className="tracking-id">{order.trackingId}</span>
                                        ) : (
                                            <span className="no-tracking">Not set</span>
                                        )}
                                    </td>
                                    <td>
                                        {order.courierPartner ? (
                                            <span className="courier-partner">{order.courierPartner}</span>
                                        ) : (
                                            <span className="no-tracking">Not set</span>
                                        )}
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-secondary btn-sm"
                                            onClick={() => handleEdit(order)}
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingOrder && (
                <div className="form-modal">
                    <div className="form-container">
                        <h3>Edit Order #{editingOrder.id}</h3>
                        <form onSubmit={handleUpdate}>
                            <div className="form-group">
                                <label htmlFor="status">Status *</label>
                                <select
                                    id="status"
                                    value={editForm.status}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                                    required
                                >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="shipped">Shipped</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="cancelled">Cancelled</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="trackingId">Tracking ID</label>
                                <input
                                    type="text"
                                    id="trackingId"
                                    value={editForm.trackingId}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, trackingId: e.target.value }))}
                                    placeholder="Enter tracking ID"
                                />
                                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                                    {editForm.status === 'shipped' && editForm.trackingId
                                        ? 'A tracking email will be sent to the customer.'
                                        : editForm.status === 'shipped'
                                            ? 'Enter tracking ID to send email to customer.'
                                            : ''}
                                </small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="courierPartner">Courier Partner</label>
                                <select
                                    id="courierPartner"
                                    value={editForm.courierPartner}
                                    onChange={(e) => setEditForm(prev => ({
                                        ...prev,
                                        courierPartner: e.target.value,
                                        customCourierName: e.target.value !== 'Other' ? '' : prev.customCourierName
                                    }))}
                                >
                                    <option value="">Select Courier Partner</option>
                                    <option value="Blue Dart">Blue Dart</option>
                                    <option value="India Post">India Post</option>
                                    <option value="DTDC">DTDC</option>
                                    <option value="FedEx">FedEx</option>
                                    <option value="DHL">DHL</option>
                                    <option value="Delhivery">Delhivery</option>
                                    <option value="XpressBees">XpressBees</option>
                                    <option value="Ecom Express">Ecom Express</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>

                            {editForm.courierPartner === 'Other' && (
                                <div className="form-group">
                                    <label htmlFor="customCourierName">Courier Name *</label>
                                    <input
                                        type="text"
                                        id="customCourierName"
                                        value={editForm.customCourierName}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, customCourierName: e.target.value }))}
                                        placeholder="Enter courier partner name"
                                        required
                                    />
                                </div>
                            )}

                            <div className="order-details">
                                <h4>Order Details:</h4>
                                <p><strong>Customer:</strong> {editingOrder.userId}</p>
                                <p><strong>Email:</strong> {editingOrder.userEmail || 'N/A'}</p>
                                <p><strong>Total:</strong> ₹{editingOrder.totalAmount?.toFixed(2)}</p>
                                <p><strong>Items:</strong></p>
                                <ul>
                                    {editingOrder.products.map((item, idx) => (
                                        <li key={idx}>
                                            {item.productName || item.productId} - Qty: {item.quantity} - ₹{(item.price * item.quantity).toFixed(2)}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="form-actions">
                                <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Update Order
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrdersManagement;

